import type { ImpactAnalysisData } from "../types/impact-analysis.ts";

type temp = "RIDICAT" | "MEDIU" | "SCAZUT";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function calculNineB(data: ImpactAnalysisData["section2_public_impact"] | any) {
  let value: temp = "SCAZUT";
  const severityRank = { SCAZUT: 0, MEDIU: 1, RIDICAT: 2 };
  function upgrade(newValue: temp) {
    if (severityRank[newValue] > severityRank[value]) {
      value = newValue;
    }
  }

  if (data.fundamental_rights) {
    if (data.fundamental_rights.personal_data_affected !== null) {
      if (data.fundamental_rights.personal_data_affected < 200000) {
        upgrade("SCAZUT");
      } else if (data.fundamental_rights.personal_data_affected <= 1000000) {
        upgrade("MEDIU");
      } else {
        upgrade("RIDICAT");
      }
    }
    if (data.fundamental_rights.public_service_access_lost !== null) {
      if (data.fundamental_rights.public_service_access_lost < 25000) {
        upgrade("SCAZUT");
      } else if (data.fundamental_rights.public_service_access_lost <= 115000) {
        upgrade("MEDIU");
      } else {
        upgrade("RIDICAT");
      }
    }
  }
  if (data.national_economy) {
    if (data.national_economy.gdp_loss_over_threshold) {
      upgrade("RIDICAT");
    }
    // Check if affects other sector services and the percentage impact
    if (
      data.national_economy.affects_other_sector_services &&
      data.national_economy.highest_sector_impact_percentage !== null
    ) {
      if (data.national_economy.highest_sector_impact_percentage >= 20) {
        upgrade("RIDICAT");
      } else if (data.national_economy.highest_sector_impact_percentage >= 5) {
        upgrade("MEDIU");
      }
    }
    // New structure uses affects_critical_infrastructure (string literal: "no" | "affected" | "destroyed")
    // Old structure used supports_critical_infrastructure + infrastructure_impact_type
    if (data.national_economy.affects_critical_infrastructure) {
      const impact = data.national_economy.affects_critical_infrastructure;

      // Handle new string literal values
      if (impact === "destroyed") {
        upgrade("RIDICAT");
      } else if (impact === "affected") {
        upgrade("MEDIU");
      } // "no" value: no upgrade

      // Backward compatibility for old boolean values
      else if (impact === true) {
        // Old boolean true: default to MEDIU (current behavior)
        upgrade("MEDIU");
      }
    } // Legacy old structure support
    else if (data.national_economy.supports_critical_infrastructure) {
      if (
        data.national_economy.infrastructure_impact_type === "functionality"
      ) {
        upgrade("MEDIU");
      } else if (
        data.national_economy.infrastructure_impact_type === "destruction"
      ) {
        upgrade("RIDICAT");
      } else {
        upgrade("MEDIU");
      }
    }
  }
  if (data.health_life) {
    // Handle new field: can_cause_death
    if (data.health_life.can_cause_death) {
      upgrade("RIDICAT");
    }
    // New structure: traumatic_injuries_or_health_impact, old: traumatic_injuries
    const traumaticInjuries =
      data.health_life.traumatic_injuries_or_health_impact ??
      data.health_life.traumatic_injuries;
    if (traumaticInjuries !== null && traumaticInjuries !== undefined) {
      if (traumaticInjuries >= 115000) {
        upgrade("RIDICAT");
      } else if (traumaticInjuries > 0) {
        upgrade("MEDIU");
      }
    }
    // New structure: chronic_illness_or_disability, old: deaths_chronic_illness
    const chronicIllness =
      data.health_life.chronic_illness_or_disability ??
      data.health_life.deaths_chronic_illness;
    if (chronicIllness && chronicIllness > 50) {
      upgrade("RIDICAT");
    }
  }
  if (data.financial_impact) {
    if (data.financial_impact.people_financial_loss !== null) {
      if (data.financial_impact.people_financial_loss < 25000) {
        upgrade("SCAZUT");
      } else if (
        data.financial_impact.people_financial_loss >= 25000 &&
        data.financial_impact.people_financial_loss < 115000
      ) {
        upgrade("MEDIU");
      } else if (data.financial_impact.people_financial_loss >= 115000) {
        upgrade("RIDICAT");
      }
    }
  }
  if (data.defense_security) {
    // New structure: affects_defense_capacity (string literal: "no" | "affected" | "destroyed")
    // Old structure: affects_state_capacity + consequence_type
    const affectsDefense =
      data.defense_security.affects_defense_capacity ??
      data.defense_security.affects_state_capacity;

    if (affectsDefense) {
      // Handle new string literal values
      if (affectsDefense === "destroyed") {
        upgrade("RIDICAT");
      } else if (affectsDefense === "affected") {
        upgrade("MEDIU");
      } // "no" value: no upgrade

      // Backward compatibility for old boolean values
      else if (affectsDefense === true) {
        // Old structure - check the three separate boolean fields
        if (data.defense_security.generates_state_incapacity) {
          upgrade("RIDICAT");
        } else if (data.defense_security.damages_state_reputation) {
          upgrade("RIDICAT");
        } else {
          // Just affects defense capacity: default to MEDIU
          upgrade("MEDIU");
        }
      } // Legacy old structure with consequence_type
      else if (data.defense_security.consequence_type === "grave") {
        upgrade("MEDIU");
      } else if (data.defense_security.consequence_type === "catastrophic") {
        upgrade("RIDICAT");
      }
    }
  }
  return value;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function calculNineC(data: ImpactAnalysisData["section3_systemic_risk"] | any) {
  let value: temp = "SCAZUT";
  const severityRank = { SCAZUT: 0, MEDIU: 1, RIDICAT: 2 };
  function upgrade(newValue: temp) {
    if (severityRank[newValue] > severityRank[value]) {
      value = newValue;
    }
  }

  // Support both old (affected_sectors_percentage) and new (highest_impact_percentage) field names
  const percentage =
    data.highest_impact_percentage ?? data.affected_sectors_percentage;
  if (data.affects_other_sectors && percentage !== null) {
    if (percentage >= 20) {
      upgrade("RIDICAT");
    } else if (percentage >= 5) {
      upgrade("MEDIU");
    }
  }

  // Check affected_sectors_count when affects_other_sectors is true
  if (
    data.affects_other_sectors &&
    data.affected_sectors_count !== null &&
    data.affected_sectors_count !== undefined
  ) {
    if (data.affected_sectors_count >= 5) {
      upgrade("RIDICAT");
    } else if (data.affected_sectors_count >= 3) {
      upgrade("MEDIU");
    }
  }

  // New structure: affects_international_obligations (string literal: "no" | "affected" | "destroyed")
  // Old structure had international_impact_type
  if (data.affects_international_obligations) {
    const impact = data.affects_international_obligations;

    // Handle new string literal values
    if (impact === "destroyed") {
      upgrade("RIDICAT");
    } else if (impact === "affected") {
      upgrade("MEDIU");
    } // "no" value: no upgrade

    // Backward compatibility for old boolean values
    else if (impact === true) {
      // Old structure defaults to MEDIU
      upgrade("MEDIU");
    } // Legacy old structure with international_impact_type
    else if (data.international_impact_type === "affected") {
      upgrade("MEDIU");
    } else if (data.international_impact_type === "annihilated") {
      upgrade("RIDICAT");
    }
  }
  return value;
}

export function algoAnalizaDeImpact(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  analizaDeImpact: ImpactAnalysisData | any
): {
  "9a": boolean | "" | null;
  "9b": temp | "" | null;
  "9c": temp | "" | null;
  "9d": boolean | "" | null;
} {
  if (!analizaDeImpact) {
    return {
      "9a": null,
      "9b": null,
      "9c": null,
      "9d": null,
    };
  }

  let nineA: boolean;
  let nineD: boolean | null;
  let nineB: temp | "";
  let nineC: temp | "";

  // Check if new structure (has section1_mandatory)
  if (analizaDeImpact.section1_mandatory) {
    // New structure
    nineA =
      analizaDeImpact.section1_mandatory.is_sole_essential_provider || false;
    nineD = analizaDeImpact.section1_mandatory.formally_designated;
    nineB = calculNineB(analizaDeImpact.section2_public_impact);
    nineC = calculNineC(analizaDeImpact.section3_systemic_risk);
  } else if (analizaDeImpact.section1_unique_provider) {
    // Old structure - migrate on the fly
    nineA = !!(
      analizaDeImpact.section1_unique_provider.anexa1_unique ||
      analizaDeImpact.section1_unique_provider.anexa2_unique ||
      analizaDeImpact.section1_unique_provider.is_sole_essential_provider
    );
    nineD = analizaDeImpact.section2_critical_importance?.formally_designated;
    nineB = calculNineB(analizaDeImpact.section3_public_impact || {});
    nineC = calculNineC(analizaDeImpact.section4_systemic_risk || {});
  } else {
    // Fallback for completely unexpected structure
    return {
      "9a": null,
      "9b": null,
      "9c": null,
      "9d": null,
    };
  }

  return {
    "9a": nineA,
    "9b": nineB,
    "9c": nineC,
    "9d": nineD,
  };
}
